import {Icon} from '@iconify/react';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {AwesomeIcon} from '@dvrd/dvr-controls';
import BolmateTooltip from '../../controls/tooltip/bolmateTooltip';

interface Props {
    rank: number | null;
    previousRank?: number | null;
    tooltip?: string;
    isSponsored?: boolean;
    showInclusief?: boolean;
}

function getChangeClass(change: number | null | undefined): string {
    if (change !== undefined && change !== null && change !== 0) {
        return change < 0 ? 'rank-improved' : 'rank-declined';
    }
    return 'rank-neutral';
}

function getPositionClass(rank: number | null): string {
    if (!rank || rank <= 0) return 'rank-default';
    if (rank <= 5) return 'rank-green';
    if (rank <= 15) return 'rank-blue';
    return 'rank-red';
}

export default function RankCell(props: Props) {
    const {rank, previousRank, isSponsored, showInclusief} = props;
    const tooltip = useMemo(() => {
        if (props.tooltip) return props.tooltip;
        if ((rank ?? 0) >= 500) return 'Product lager dan positie 500 of product niet aangeboden.';
    }, [props.tooltip, rank]);
    const {t} = useTranslation();
    const change = useMemo(() => {
        if (typeof previousRank === 'number' && typeof rank === 'number') {
            if (previousRank === 0) return null;
            return rank - previousRank;
        }
        return null;
    }, [previousRank, rank]);
    const displayRank = useMemo(() => {
        if (!rank) return '\u2014';
        if (rank >= 500) return '500+';
        return rank;
    }, [rank]);
    const rankClass = `${getPositionClass(rank)} ${getChangeClass(change)}`;

    const hasChange = change !== undefined && change !== null && change !== 0 && rank && rank > 0;
    // Negative change = improved (rank went down = better position)
    const isImproved = hasChange && change < 0;

    return (
        <BolmateTooltip text={tooltip}>
            <div className={`rank-cell ${rankClass}`}>
                <div className='rank-value-row'>
                    <span className='rank-number'>{displayRank}</span>
                    {hasChange && (
                        <span className={`rank-delta ${isImproved ? 'improved' : 'declined'}`}>
                            <Icon
                                icon='mdi:triangle'
                                style={{
                                    fontSize: '0.5rem',
                                    transform: isImproved ? 'none' : 'rotate(180deg)'
                                }}
                            />
                            {Math.abs(change)}
                        </span>
                    )}
                </div>
                {rank !== null && rank > 0 && (isSponsored || showInclusief) && (
                    <BolmateTooltip
                        text={
                            showInclusief
                                ? t('searchRankings.table.rankInclusiveSponsored', 'Inclusief sponsored')
                                : t('searchRankings.table.rankSponsoredPosition', 'Sponsored positie')
                        }
                    >
                        <span className='rank-sponsored-badge'>
                            <AwesomeIcon name='bullhorn' className='rank-sponsored-icon' />
                        </span>
                    </BolmateTooltip>
                )}
            </div>
        </BolmateTooltip>
    );
}
